/**
 * SAB-1038: shaping the waiver results log.
 *
 * The RPC returns a flat, already-ordered list. This groups it into the shape the page
 * renders — run, then player, then winner-first rows — and turns each row's stored facts
 * into the sentence a manager reads.
 *
 * ── THE REASON STRINGS ARE THE SAME VOCABULARY AS THE NOTIFICATIONS ─────────
 *
 * SAB-1027 established what a losing manager is told: outbid names the winning bid, a tie
 * cites the standings rule, a failure carries its typed reason. The public log has to say
 * the SAME thing, or a manager reads one story in their bell and a different one here —
 * which is the disclosed-vs-actual defect this whole arc has been closing.
 */

export interface WaiverResultRow {
  id: string;
  run_date: string;
  processed_at: string;
  player_id: string;
  player_name: string;
  player_position: string | null;
  team_name: string;
  faab_bid: number | string;
  resolution_priority: number | null;
  status: 'awarded' | 'outbid' | 'failed' | string;
  failure_code: string | null;
  drop_player_name: string | null;
  drop_player_position: string | null;
  lost_on_tie: boolean | null;
  winning_bid: number | string | null;
}

export interface PlayerGroup {
  playerId: string;
  playerName: string;
  playerPosition: string | null;
  rows: WaiverResultRow[];
}

export interface RunGroup {
  runDate: string;
  players: PlayerGroup[];
}

/**
 * Group flat rows into runs, then players, preserving the RPC's ordering.
 *
 * Insertion order is load-bearing: the RPC already sorted winner-first within each player
 * and newest-run-first overall, so re-sorting here would be a second opinion about an order
 * the server already decided.
 */
export function groupWaiverResults(rows: readonly WaiverResultRow[]): RunGroup[] {
  const runs = new Map<string, Map<string, PlayerGroup>>();
  for (const row of rows) {
    let players = runs.get(row.run_date);
    if (!players) { players = new Map(); runs.set(row.run_date, players); }
    let group = players.get(row.player_id);
    if (!group) {
      group = {
        playerId: row.player_id,
        playerName: row.player_name,
        playerPosition: row.player_position,
        rows: [],
      };
      players.set(row.player_id, group);
    }
    group.rows.push(row);
  }
  return [...runs.entries()].map(([runDate, players]) => ({
    runDate,
    players: [...players.values()],
  }));
}

const FAILURE_TEXT: Record<string, string> = {
  NO_ROSTER_SLOT: 'no roster spot the player could fill',
  POSITION_NOT_ROSTERABLE: 'that position cannot start in this league',
  POSITION_LIMIT_REACHED: 'at the league limit for that position',
  FAAB_INSUFFICIENT: 'not enough FAAB left',
  ADD_FAILED: 'the roster move could not be completed',
  ALREADY_ROSTERED: 'the player was already rostered',
};

/**
 * What happened to this claim, in the manager's words.
 *
 * An UNRECOGNISED failure_code degrades to a generic line rather than rendering blank or
 * guessing — the column is deliberately unconstrained (SAB-1029) so new codes are expected,
 * and guessing "roster full" for a budget failure teaches the wrong lesson about your team.
 * A NULL code on a failed row means the reason predates the column, and says so.
 */
export function resultLine(row: WaiverResultRow): string {
  const bid = Number(row.faab_bid) || 0;
  if (row.status === 'awarded') {
    return row.drop_player_name
      ? `Won for $${bid} — dropped ${row.drop_player_name}`
      : `Won for $${bid}`;
  }
  if (row.status === 'outbid') {
    if (row.lost_on_tie) {
      // The rule, stated. A tie loser told only "outbid" would draw exactly the wrong
      // conclusion about what to do next — bid higher, when the lever was the standings.
      return `Tied at $${bid} — ties go to the team lowest in the standings`;
    }
    const winning = row.winning_bid == null ? null : Number(row.winning_bid);
    return winning == null
      ? `Outbid — bid $${bid}`
      : `Outbid — bid $${bid}, winning bid $${winning}`;
  }
  if (row.status === 'failed') {
    if (!row.failure_code) return `Did not process — reason not recorded ($${bid} bid)`;
    const known = FAILURE_TEXT[row.failure_code];
    return known ? `Failed — ${known} ($${bid} bid)` : `Failed — ${row.failure_code} ($${bid} bid)`;
  }
  return `$${bid}`;
}

/* ------------------------------------------------------------------ */
/*  SAB-1208: the claim-detail modal                                   */
/* ------------------------------------------------------------------ */

/**
 * Pick the run a feed row refers to.
 *
 * `runDate` comes from the feed row's metadata, written by nfl-process-waivers at insert.
 * Rows written BEFORE SAB-1208 do not carry it, and there is no honest way to reconstruct
 * one -- so they fall back to the newest run for that player, which is what a manager
 * clicking a recent feed row means anyway.
 *
 * Returns null only when the player has no resolved claims at all, which the modal renders
 * as a stated absence rather than an empty card.
 */
export function pickRunGroup(runs: readonly RunGroup[], runDate: string | null): RunGroup | null {
  if (runs.length === 0) return null;
  if (runDate) {
    const exact = runs.find(r => r.runDate === runDate);
    // A stamped run_date that matches nothing means the claim was later deleted or the
    // stamp is wrong. Falling through to the newest run would show a DIFFERENT run under
    // the date the feed row named, so the caller is told there is no match instead.
    if (exact) return exact;
    return null;
  }
  return runs[0];
}

/**
 * How many resolved claims competed for this player in this run.
 *
 * This is the number the feed row's "N bids" advertises, and it is derived from the SAME
 * rows the modal lists -- so the count and the list cannot disagree. A count computed from
 * anything else is how a feed row comes to promise three bids over a modal showing two.
 */
export function bidCount(group: PlayerGroup): number {
  return group.rows.length;
}

/**
 * Re-exported, NOT reimplemented.
 *
 * nfl-process-waivers writes the ", N bids" suffix into the feed title on the server. If the
 * client owned a second copy of that rule, the two would drift the first time one was
 * edited -- and the drift would show as a feed row promising a bid count the modal below it
 * contradicts. One definition, reached through the @shared alias.
 */
export { bidCountSuffix } from '@shared/waiverBidCount';
